import { useKeyModificationDialogStore } from 'components/WorkspaceEditor/components/KeyModificationWarningDialog'
import { KEY_MODIFICATION_WARNING_DISMISSED_KEY } from 'components/WorkspaceEditor/constants/keyModification'
import { useCallback, useEffect, useRef } from 'react'
import { hasKeyChanged } from '../utils/keyChangeDetection'

/**
 * Options for split key scenarios where the key is distributed across multiple elements
 */
export interface SplitKeyOptions {
    /** Whether this is a split key element (first element without colon) */
    isSplitKeyElement: boolean
    /** The full keyed text (e.g., "Label: ") to show in dialog */
    fullKeyedText: string
}

/**
 * Hook to handle key change confirmation dialog
 * Shows a confirmation dialog when a keyed text's key portion is modified
 */
export const useKeyChangeConfirmation = () => {
    const { showDialog, closeDialog } = useKeyModificationDialogStore(
        (state) => ({
            showDialog: state.showDialog,
            closeDialog: state.closeDialog,
        })
    )

    // Store the resolve function for the promise
    const resolveRef = useRef<((value: boolean) => void) | null>(null)

    // Cleanup on unmount to prevent memory leaks
    useEffect(() => {
        return () => {
            // If component unmounts while dialog is open, resolve with false (cancel)
            if (resolveRef.current) {
                resolveRef.current(false)
                resolveRef.current = null
            }
        }
    }, [])

    /**
     * Checks if key was changed and shows confirmation dialog if needed
     * @param originalText - The original text before editing
     * @param newText - The new text after editing
     * @param splitKeyOptions - Optional options for split key scenarios
     * @returns Promise that resolves to true if user confirmed or no change, false if cancelled
     */
    const checkAndConfirmKeyChange = useCallback(
        (originalText: string, newText: string, splitKeyOptions?: SplitKeyOptions): Promise<boolean> => {
            const isSplitKey = splitKeyOptions?.isSplitKeyElement && splitKeyOptions?.fullKeyedText

            // Check if key changed (for split keys, any text change is a key change)
            if (!isSplitKey && !hasKeyChanged(originalText, newText)) {
                // No key change, proceed without confirmation
                return Promise.resolve(true)
            }

            // For split keys, check if text actually changed
            if (isSplitKey && originalText === newText) {
                return Promise.resolve(true)
            }

            // Check if user has dismissed this warning for this session
            const isDismissed = sessionStorage.getItem(KEY_MODIFICATION_WARNING_DISMISSED_KEY) === 'true'
            if (isDismissed) {
                // User has opted to not show this warning, proceed without dialog
                return Promise.resolve(true)
            }

            // Key changed, show confirmation dialog
            return new Promise<boolean>((resolve) => {
                resolveRef.current = resolve

                const extractKey = (text: string) => {
                    const colonIndex = text.indexOf(':')
                    return colonIndex !== -1 ? text.substring(0, colonIndex + 1) : text
                }

                // For split keys, use the full keyed text as the original key
                // and construct a modified key showing what part changed
                const originalKey = isSplitKey
                    ? splitKeyOptions.fullKeyedText
                    : extractKey(originalText)

                // For split keys, show the modified element text to indicate the change
                // The user is editing part of the key that doesn't contain the colon
                const modifiedKey = isSplitKey
                    ? splitKeyOptions.fullKeyedText.replace(originalText.trim(), newText.trim())
                    : extractKey(newText)

                showDialog({
                    isPTKey: false, // This is for base content key modification
                    originalKey,
                    modifiedKey,
                    onProceed: () => {
                        closeDialog()
                        resolveRef.current?.(true)
                        resolveRef.current = null
                    },
                    onCancel: () => {
                        closeDialog()
                        resolveRef.current?.(false)
                        resolveRef.current = null
                    },
                })
            })
        },
        [showDialog, closeDialog]
    )

    return { checkAndConfirmKeyChange }
}
